import { CSSProperties, ReactNode, useEffect } from 'react';
import Icon from '../icons/icon';
import { Typography } from '../typography/typography';
import './modal.scss';
import { animated, config, useTransition } from 'react-spring';
import { Portal } from '../portal/portal';
import classNames from 'classnames';

export type ModalProps = {
  isOpen: boolean;
  headerIcon?: ReactNode;
  title?: string;
  additionalText?: ReactNode;
  style?: CSSProperties;
  children?: ReactNode;
  actions?: ReactNode;
  zIndex?: number;
  className?: string;
  overflow?: string;
  close?: () => any;
};

export const Modal = ({
  isOpen,
  headerIcon,
  title,
  additionalText,
  children,
  style = {},
  actions,
  zIndex = 1000,
  className,
  overflow,
  close,
}: ModalProps) => {
  const transition = useTransition(isOpen, {
    from: {
      opacity: 0,
      transform: 'translateY(-40px)',
    },
    enter: {
      opacity: 1,
      transform: 'translateY(0px)',
    },
    leave: {
      opacity: 0,
      transform: 'translateY(-40px)',
    },
    config: config.gentle,
  });

  useEffect(() => {
    const onKeyEscPressed = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close?.();
      }
    };
    window.addEventListener('keydown', onKeyEscPressed);
    return () => window.removeEventListener('keydown', onKeyEscPressed);
  }, []);

  return transition(
    (transitionStyle, isOpen) =>
      isOpen && (
        <>
          <Portal zIndex={zIndex}>
            <animated.div
              style={{ opacity: transitionStyle.opacity, zIndex }}
              className="dialog-overlay"
              onClick={() => close?.()}
            />

            <div className="centered max-w-full" style={{ zIndex: zIndex + 1, overflow }}>
              <animated.div style={{ ...transitionStyle, ...style }} className={classNames('dialog', className)}>
                {/* Close */}
                {close && (
                  <button type="button" className="close-icon" onClick={() => close?.()}>
                    <Icon icon="Close" stroke="var(--grey-600)" />
                  </button>
                )}

                <div className="dialog-content">
                  {headerIcon && <div className="flex justify-center">{headerIcon}</div>}

                  {title && (
                    <div className="dialog-header">
                      <Typography type="display-xs" weight="semi-bold" className="text-grey-700">
                        {title}
                      </Typography>
                    </div>
                  )}

                  {additionalText && (
                    <div className="text-center px-4">
                      <Typography type="text-sm" className="text-grey-500">
                        {additionalText}
                      </Typography>
                    </div>
                  )}

                  {children}

                  {actions && <div className="flex flex-col lg:flex-row [&>*]:!grow gap-3">{actions}</div>}
                </div>
              </animated.div>
            </div>
          </Portal>
        </>
      ),
  );
};
